import { Box, Typography, useTheme } from "@mui/material";
import * as countries from "country-list";
import React, { useMemo } from "react";
import { useAppConfig } from "rwo-react";
import { parseDimension } from "../../../domain/mappers/metricsMappers";
import type { Dimension } from "../../../domain/models/metrics";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { useLocalization } from "../../../hooks/useLocalization";
import { useSessionStorageState } from "../../../hooks/useSessionStorageState";
import { ActivityLineChart } from "./components/ActivityLineChart";
import { ActivitySummaryCards } from "./components/ActivitySummaryCards";
import { ActivityTrendChart } from "./components/ActivityTrendChart";
import { CountryUsageCard } from "./components/CountryUsageCard";
import { FeedbackRatingsCard } from "./components/FeedbackRatingsCard";
import { FilterToolbar } from "./components/FilterToolbar";
import { StickyToolbarWrapper } from "./components/StickyToolbarWrapper";
import { UsagePerExperience } from "./components/UsagePerExperience";
import {
  CUMULATIVE_CHARTS_FEATURE_FLAG,
  DEFAULT_DIMENSION,
  DEFAULT_END_DATE,
  DEFAULT_START_DATE,
  DIMENSION_KEY,
  END_DATE_KEY,
  MIN_SESSION_DURATION_KEY,
  SELECTED_COUNTRIES_KEY,
  SELECTED_EXPERIENCES_KEY,
  START_DATE_KEY,
} from "./constants";
import { useActivityTrendData, useExperienceOptions } from "./hooks";
import { useCountryCertificationsData } from "./hooks/useCountryCertificationsData";
import { useCountryDataMetrics } from "./hooks/useCountryDataMetrics";
import { useCountryHoursData } from "./hooks/useCountryHoursData";
import { useCountrySessionsData } from "./hooks/useCountrySessionsData";
import { useExperienceDataMetrics } from "./hooks/useExperienceDataMetrics";
import { useInternalChartData } from "./hooks/useInternalChartData";
import { useInternalFeedbackRatings } from "./hooks/useInternalFeedbackRatings";
import { parseMinSessionDuration } from "./utils";

const FEEDBACK_FEATURE_FLAG = "uam_ShowFeedback";
const CERTIFICATIONS_FEATURE_FLAG = "uam_showCertifications";

export interface InternalUsageTabProps {
  isUserLevelDataEnabled?: boolean;
  hasTabs?: boolean;
}

export const InternalUsageTab: React.FC<InternalUsageTabProps> = ({
  isUserLevelDataEnabled = false,
  hasTabs = true,
}) => {
  const [selectedCountries, setSelectedCountries] = useSessionStorageState<
    string[]
  >(SELECTED_COUNTRIES_KEY, []);
  const [selectedExperiences, setSelectedExperiences] = useSessionStorageState<
    string[]
  >(SELECTED_EXPERIENCES_KEY, []);
  const [dimension, setDimension] = useSessionStorageState<Dimension>(
    DIMENSION_KEY,
    DEFAULT_DIMENSION,
    parseDimension,
  );
  const [startDate, setStartDate] = useSessionStorageState<Date>(
    START_DATE_KEY,
    DEFAULT_START_DATE,
  );
  const [endDate, setEndDate] = useSessionStorageState<Date>(
    END_DATE_KEY,
    DEFAULT_END_DATE,
  );
  const [minSessionDuration, setMinSessionDuration] = useSessionStorageState<
    number | null
  >(MIN_SESSION_DURATION_KEY, null, parseMinSessionDuration);

  const { experienceOptions } = useExperienceOptions();

  const contentMetadataIdsFilterValue = useMemo(() => {
    if (
      selectedExperiences.length === 0 ||
      selectedExperiences.length === experienceOptions.length
    ) {
      return undefined;
    }

    return selectedExperiences;
  }, [selectedExperiences, experienceOptions]);

  const countriesFilterValue = useMemo(() => {
    if (
      selectedCountries.length === 0 ||
      selectedCountries.length === countries.getData().length
    ) {
      return undefined;
    }

    return selectedCountries;
  }, [selectedCountries]);

  const filter = useMemo(
    () => ({
      startDate,
      endDate,
      dimension,
      contentMetadataIds: contentMetadataIdsFilterValue,
      countries: countriesFilterValue,
      minSessionDurationInMinutes: minSessionDuration ?? undefined,
    }),
    [
      dimension,
      countriesFilterValue,
      contentMetadataIdsFilterValue,
      startDate,
      endDate,
      minSessionDuration,
    ],
  );
  const isMobile = useIsMobile();
  const ShowUsageChart = !isMobile;

  const { featureFlags } = useAppConfig();
  const showFeedback = Boolean(featureFlags[FEEDBACK_FEATURE_FLAG]);
  const showCertifications = Boolean(featureFlags[CERTIFICATIONS_FEATURE_FLAG]);
  const showCumulativeCharts = Boolean(
    featureFlags[CUMULATIVE_CHARTS_FEATURE_FLAG],
  );

  const {
    chartData,
    legendValues,
    isLoading: isActivityLoading,
    isFetching: isActivityFetching,
  } = useInternalChartData(filter);

  const {
    chartData: activityTrendData,
    isLoading: isActivityTrendLoading,
    isFetching: isActivityTrendFetching,
  } = useActivityTrendData({ ...filter, enabled: showCumulativeCharts });

  const {
    data: experienceDataRaw,
    isLoading: isExperienceDataLoading,
    isFetching: isExperienceDataFetching,
  } = useExperienceDataMetrics({
    ...filter,
    enabled: ShowUsageChart,
  });

  // Backfill selected experiences the API omitted (zero usage)
  const experienceData = useMemo(() => {
    if (!contentMetadataIdsFilterValue) return experienceDataRaw;

    const returnedIds = new Set(experienceDataRaw.map((d) => d.id));
    const missing = contentMetadataIdsFilterValue
      .filter((id) => !returnedIds.has(id))
      .map((id) => ({
        id,
        name:
          experienceOptions.find((o) => o.value === id)?.label ??
          `Experience ${id.slice(0, 8)}`,
        hoursInVr: 0,
        userSessions: 0,
        uniqueActiveUsers: 0,
        certifications: 0,
      }));

    return missing.length > 0
      ? [...experienceDataRaw, ...missing]
      : experienceDataRaw;
  }, [experienceDataRaw, contentMetadataIdsFilterValue, experienceOptions]);

  const selectedLicenseNames = useMemo(() => {
    if (!contentMetadataIdsFilterValue) return [];

    return contentMetadataIdsFilterValue
      .map((id) => experienceOptions.find((o) => o.value === id)?.label)
      .filter((label): label is string => !!label);
  }, [contentMetadataIdsFilterValue, experienceOptions]);

  // ── Country data (sessions, hours & certifications per country) ──────
  const {
    sessionEntries: countrySessionsRaw,
    hoursEntries: countryHoursRaw,
    certificationEntries: countryCertificationsRaw,
    isLoading: isCountryDataLoading,
    isFetching: isCountryDataFetching,
    error: countryDataError,
  } = useCountryDataMetrics(filter);

  const { rows: countrySessionRows, isEmpty: isCountrySessionsEmpty } =
    useCountrySessionsData(
      countrySessionsRaw,
      legendValues.userSessions > 0 ? legendValues.userSessions : undefined,
      isUserLevelDataEnabled,
    );
  const { rows: countryHoursRows, isEmpty: isCountryHoursEmpty } =
    useCountryHoursData(
      countryHoursRaw,
      legendValues.hoursInVr > 0 ? legendValues.hoursInVr : undefined,
      isUserLevelDataEnabled,
    );
  const {
    rows: countryCertificationRows,
    isEmpty: isCountryCertificationsEmpty,
  } = useCountryCertificationsData(
    countryCertificationsRaw,
    isUserLevelDataEnabled,
  );

  // ── Feedback Ratings ──────────────────────────────────────────────
  const {
    data: feedbackData,
    overallRating: feedbackOverallRating,
    isLoading: isFeedbackLoading,
    isFetching: isFeedbackFetching,
  } = useInternalFeedbackRatings({
    startDate,
    endDate,
    contentMetadataIds: contentMetadataIdsFilterValue,
    countries: countriesFilterValue,
    enabled: Boolean(showFeedback),
    experienceOptions: experienceData.map((d) => ({
      value: d.id,
      label: d.name,
    })),
  });

  const theme = useTheme();
  const { t } = useLocalization();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
        pb: 3,
      }}
    >
      <StickyToolbarWrapper hasTabs={hasTabs}>
        <FilterToolbar
          dimension={dimension}
          endDate={endDate}
          minSessionDuration={minSessionDuration}
          onCountriesChange={setSelectedCountries}
          onDimensionChange={setDimension}
          onExperiencesChange={setSelectedExperiences}
          onMinSessionDurationChange={setMinSessionDuration}
          selectedCountries={selectedCountries}
          selectedExperiences={selectedExperiences}
          setEndDate={setEndDate}
          setStartDate={setStartDate}
          startDate={startDate}
        />
      </StickyToolbarWrapper>

      <ActivitySummaryCards
        aggregateValues={legendValues}
        isFetching={isActivityFetching}
        isLoading={isActivityLoading}
        isUserLevelDataEnabled={isUserLevelDataEnabled}
      />

      <ActivityLineChart
        data={chartData}
        endDate={endDate}
        isFetching={isActivityFetching}
        isLoading={isActivityLoading}
        isUserLevelDataEnabled={isUserLevelDataEnabled}
        selectedLicenseNames={selectedLicenseNames}
        startDate={startDate}
      />

      {showCumulativeCharts && (
        <ActivityTrendChart
          data={activityTrendData}
          endDate={endDate}
          isFetching={isActivityTrendFetching}
          isLoading={isActivityTrendLoading}
          isUserLevelDataEnabled={isUserLevelDataEnabled}
          selectedLicenseNames={selectedLicenseNames}
          startDate={startDate}
        />
      )}

      {/* Usage per Experience Section */}
      {ShowUsageChart && (
        <UsagePerExperience
          data={experienceData}
          endDate={endDate}
          isFetching={isExperienceDataFetching}
          isLoading={isExperienceDataLoading}
          isUserLevelDataEnabled={isUserLevelDataEnabled}
          showCertifications={showCertifications}
          startDate={startDate}
        />
      )}

      {/* Feedback Ratings Section */}
      {showFeedback && (
        <FeedbackRatingsCard
          data={feedbackData}
          endDate={endDate}
          isFetching={isFeedbackFetching}
          isLoading={isFeedbackLoading}
          isUserLevelDataEnabled={isUserLevelDataEnabled}
          overallRating={feedbackOverallRating}
          startDate={startDate}
        />
      )}

      {/* Country Usage Section */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 3,
          alignItems: "flex-start",
        }}
      >
        <CountryUsageCard
          barColor={theme.colors.metrics.userSessions}
          endDate={endDate}
          isEmpty={isCountrySessionsEmpty}
          isError={Boolean(countryDataError)}
          isFetching={isCountryDataFetching}
          isLoading={isCountryDataLoading}
          isUserLevelDataEnabled={isUserLevelDataEnabled}
          rows={countrySessionRows}
          startDate={startDate}
          title={t(
            "pages.dataAnalytics.internalUsage.countryUsage.userSessionsTitle",
          )}
        />
        <CountryUsageCard
          barColor={theme.colors.metrics.hoursInVr}
          endDate={endDate}
          isEmpty={isCountryHoursEmpty}
          isError={Boolean(countryDataError)}
          isFetching={isCountryDataFetching}
          isLoading={isCountryDataLoading}
          isUserLevelDataEnabled={isUserLevelDataEnabled}
          rows={countryHoursRows}
          startDate={startDate}
          title={t(
            "pages.dataAnalytics.internalUsage.countryUsage.hoursInVrTitle",
          )}
        />
        {showCertifications && (
          <CountryUsageCard
            barColor={theme.colors.metrics.certifications}
            endDate={endDate}
            isEmpty={isCountryCertificationsEmpty}
            isError={Boolean(countryDataError)}
            isFetching={isCountryDataFetching}
            isLoading={isCountryDataLoading}
            isUserLevelDataEnabled={isUserLevelDataEnabled}
            rows={countryCertificationRows}
            startDate={startDate}
            title={t(
              "pages.dataAnalytics.internalUsage.countryUsage.certificationsTitle",
            )}
          />
        )}
      </Box>

      {isMobile && (
        <Typography
          sx={{
            color: theme.colors.text[1],
            textAlign: "center",
            fontSize: 14,
            fontWeight: 500,
            px: 2,
            pb: 8,
          }}
        >
          {t("pages.dataAnalytics.internalUsage.mobileDisclaimer")}
        </Typography>
      )}
    </Box>
  );
};
